// The signed-in person's session (CLIENT-ARCHITECTURE §3, web PLATFORM-SPEC §2).
//
// - The 15-minute access token lives in memory only.
// - The refresh token is handed to the BFF straight after sign-in. The BFF keeps
//   it in an HttpOnly, SameSite=Strict cookie that page scripts can't read, and
//   refreshes through the API on our behalf.
// - Only one refresh runs at a time, across every tab (Web Locks), because
//   reusing a rotated refresh token revokes the whole session.
//
// Local development without the BFF running: the refresh token is kept in
// memory for this tab only (signing in again after a reload), with a warning.
import { API_BASE_URL, API_VERSION } from "./config";

export interface AuthSession {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  session_id: string;
  user_id: string;
  email_verified?: boolean;
}

interface State {
  accessToken: string | null;
  expiresAt: number;
  userId: string | null;
  sessionId: string | null;
  testMode: boolean;
}

const CSRF_HEADER = { "X-Substratal-CSRF": "1" };
const listeners = new Set<() => void>();
let state: State = { accessToken: null, expiresAt: 0, userId: null, sessionId: null, testMode: false };
let memoryRefreshToken: string | null = null; // dev fallback only
let refreshTimer: ReturnType<typeof setTimeout> | undefined;
let inFlight: Promise<boolean> | null = null;
// Whether this page load has already tried to resume a session from the BFF cookie.
let restoreAttempt: Promise<boolean> | null = null;

function set(next: Partial<State>) {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

function scheduleRefresh() {
  clearTimeout(refreshTimer);
  const ms = Math.max(5_000, state.expiresAt - Date.now() - 60_000);
  refreshTimer = setTimeout(() => {
    if (document.visibilityState === "visible") void session.refresh();
    else document.addEventListener("visibilitychange", () => void session.refresh(), { once: true });
  }, ms);
}

function decodeTestMode(jwt: string): boolean {
  try {
    const payload = JSON.parse(atob(jwt.split(".")[1]!.replace(/-/g, "+").replace(/_/g, "/")));
    return payload.test_mode === true;
  } catch {
    return false;
  }
}

function accept(s: Pick<AuthSession, "access_token" | "expires_in"> & Partial<AuthSession>) {
  set({
    accessToken: s.access_token,
    expiresAt: Date.now() + s.expires_in * 1000,
    userId: s.user_id ?? state.userId,
    sessionId: s.session_id ?? state.sessionId,
    testMode: decodeTestMode(s.access_token),
  });
  scheduleRefresh();
}

async function withLock<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator !== "undefined" && navigator.locks) return navigator.locks.request("substratal-session-refresh", fn);
  return fn();
}

async function refreshViaBff(): Promise<AuthSession | null> {
  const res = await fetch("/bff/session/refresh", { method: "POST", headers: CSRF_HEADER, credentials: "same-origin" });
  if (!res.ok) return null;
  return res.json();
}

async function refreshInMemory(): Promise<AuthSession | null> {
  if (!memoryRefreshToken) return null;
  const res = await fetch(`${API_BASE_URL}/auth/token/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Substratal-Version": API_VERSION },
    body: JSON.stringify({ refresh_token: memoryRefreshToken }),
  });
  if (!res.ok) return null;
  const s: AuthSession = await res.json();
  memoryRefreshToken = s.refresh_token;
  return s;
}

export const session = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  snapshot: () => state,
  accessToken: () => state.accessToken,
  isSignedIn: () => state.accessToken !== null,

  /** Call with the AuthSession from login, MFA verification or invitation acceptance. */
  async start(s: AuthSession): Promise<void> {
    accept(s);
    try {
      const res = await fetch("/bff/session", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...CSRF_HEADER },
        credentials: "same-origin",
        body: JSON.stringify({ refresh_token: s.refresh_token }),
      });
      if (!res.ok) throw new Error(`BFF returned ${res.status}`);
      memoryRefreshToken = null;
    } catch (err) {
      if (!import.meta.env.DEV) throw err;
      console.warn("BFF unavailable; keeping the refresh token in memory for this tab (development only).", err);
      memoryRefreshToken = s.refresh_token;
    }
  },

  /** Renews the access token once, shared by every caller and tab. */
  refresh(): Promise<boolean> {
    inFlight ??= withLock(async () => {
      const s = memoryRefreshToken ? await refreshInMemory() : await refreshViaBff().catch(() => null);
      if (s) accept(s);
      else session.clear();
      return Boolean(s);
    }).finally(() => {
      inFlight = null;
    });
    return inFlight;
  },

  /**
   * Signed in now? Resumes the session from the BFF cookie at most once per page
   * load, so signed-out visitors browsing the help center don't call the BFF on
   * every navigation. Afterwards the answer is simply whether there's a token.
   */
  async restore(): Promise<boolean> {
    if (state.accessToken) return true;
    if (!restoreAttempt) {
      restoreAttempt = session.refresh();
      return restoreAttempt;
    }
    await restoreAttempt;
    return state.accessToken !== null;
  },

  async signOut(): Promise<void> {
    try {
      if (memoryRefreshToken) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Substratal-Version": API_VERSION, Authorization: `Bearer ${state.accessToken}` },
          body: "{}",
        });
      } else {
        await fetch("/bff/session/logout", { method: "POST", headers: { ...CSRF_HEADER, Authorization: `Bearer ${state.accessToken}` }, credentials: "same-origin" });
      }
    } finally {
      session.clear();
    }
  },

  clear() {
    clearTimeout(refreshTimer);
    memoryRefreshToken = null;
    set({ accessToken: null, expiresAt: 0, userId: null, sessionId: null, testMode: false });
  },
};
